import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SchoolIcon from "@mui/icons-material/School";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";

export default function MarkAttendance() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const getToday = () => new Date().toISOString().split("T")[0];

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [role, setRole] = useState("");
  const [branchList, setBranchList] = useState([]);
  const [classList, setClassList] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [date, setDate] = useState(getToday);

  const [reasonDialog, setReasonDialog] = useState({
    open: false,
    studentId: null,
    reason: "",
  });

  const [errors, setErrors] = useState({
    selectedClass: "",
    date: "",
    attendance: "",
  });

  const [editStudent, setEditStudent] = useState(null);
  const [tempAtt, setTempAtt] = useState({ status: "present", check_in: "", check_out: "", reason: "" });

  /* -----------------------------------------------------------
     LOAD USER ROLE
  ------------------------------------------------------------*/
  useEffect(() => {
    fetch(`${apiEndpoints.attendance}?action=staff-info`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message, "error");
          return;
        }

        setRole(res.role);

        if (res.role === "staff") {
          setSelectedBranch(res.branch_guid);
          setSelectedClass(res.assigned_class);
          loadClasses(res.branch_guid);
        }

        if (res.role === "admin") {
          loadBranches();
        }
      });
  }, []);

  /* -----------------------------------------------------------
     LOAD BRANCHES (ADMIN)
  ------------------------------------------------------------*/
  const loadBranches = () => {
    fetch(`${apiEndpoints.attendance}?action=branches`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.status) setBranchList(res.data);
      });
  };
  console.log(branchList);
  /* -----------------------------------------------------------
     LOAD CLASSES
  ------------------------------------------------------------*/
  const loadClasses = (branchGuid) => {
    fetch(
      `${apiEndpoints.attendance}?action=classes&branch_guid=${branchGuid}`,
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      },
    )
      .then((res) => res.json())
      .then((res) => {
        if (res.status) setClassList(res.data);
      });
  };

  /* -----------------------------------------------------------
     LOAD STUDENTS
  ------------------------------------------------------------*/
  useEffect(() => {
    if (!selectedBranch || !selectedClass || !date) return;

    const controller = new AbortController();

    fetch(
      `${apiEndpoints.attendance}?action=students&branch_guid=${selectedBranch}&class_guid=${selectedClass}&date=${date}`,
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        signal: controller.signal,
      },
    )
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message, "error");
          setStudents([]);
          return;
        }

        setStudents(res.students);

        const init = {};
        res.students.forEach((stu) => {
          init[stu.guid] = {
            status: stu.status || "present",
            reason: stu.reason || "",
            check_in: stu.check_in ? stu.check_in.substring(0, 5) : "",
            check_out: stu.check_out ? stu.check_out.substring(0, 5) : "",
          };
        });

        setAttendance(init);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          showSnackbar("Failed to load students", "error");
        }
      });

    return () => controller.abort();
  }, [selectedBranch, selectedClass, date]);

  const saveSingleStudentAttendance = (studentGuid, updatedAtt) => {
    if (!selectedBranch || !selectedClass || !date) return;

    const record = {
      student_guid: studentGuid,
      status: updatedAtt.status || "present",
      remarks: updatedAtt.reason || "",
      check_in: updatedAtt.check_in || "",
      check_out: updatedAtt.check_out || "",
    };

    fetch(`${apiEndpoints.attendance}?action=insert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        date,
        branch_guid: selectedBranch,
        class_guid: selectedClass,
        records: [record],
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message || "Failed to auto-save to database", "error");
        }
      })
      .catch(() => {
        showSnackbar("Network error saving attendance", "error");
      });
  };

  /* -----------------------------------------------------------
     VALIDATION
  ------------------------------------------------------------*/
  const validateForm = () => {
    let newErrors = { selectedClass: "", date: "", attendance: "" };
    let hasError = false;

    if (!selectedClass) {
      newErrors.selectedClass = "Please select a class.";
      hasError = true;
    }

    if (!date) {
      newErrors.date = "Please select a date.";
      hasError = true;
    }

    const incomplete = students.some((s) => !attendance[s.guid]?.status);

    if (incomplete) {
      newErrors.attendance = "Please mark attendance for all students.";
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  /* -----------------------------------------------------------
     SUBMIT
  ------------------------------------------------------------*/
  const submitAttendance = () => {
    if (!validateForm()) return;

    const records = students.map((stu) => ({
      student_guid: stu.guid,
      status: attendance[stu.guid]?.status || "present",
      remarks: attendance[stu.guid]?.reason || "",
      check_in: attendance[stu.guid]?.check_in || "",
      check_out: attendance[stu.guid]?.check_out || "",
    }));

    fetch(`${apiEndpoints.attendance}?action=insert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        date,
        branch_guid: selectedBranch,
        class_guid: selectedClass,
        records,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message, "error");
          return;
        }

        showSnackbar("Attendance saved successfully!", "success");
        navigate("/teacher/attendance");
      });
  };

  /* -----------------------------------------------------------
     UI
  ------------------------------------------------------------*/
  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight={700} mb={3}>
        <SchoolIcon sx={{ mr: 1 }} />
        Mark Attendance
      </Typography>

      <Card sx={{ p: 3, mb: 4, borderRadius: "16px" }}>
        <Grid container spacing={3}>
          {/* ADMIN BRANCH */}
          {role === "admin" && (
            <Grid item xs={12} sm={6} borderRadius="16px">
              <Typography fontWeight={600} mb={1}>
                Select Branch
              </Typography>
              <Select
                fullWidth
                sx={{
                  borderRadius: "999px",
                }}
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  setSelectedClass("");
                  loadClasses(e.target.value);
                }}
              >
                <MenuItem value="">Select Branch</MenuItem>
                {branchList.map((b) => (
                  <MenuItem key={b.guid} value={b.guid}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          {/* CLASS */}
          <Grid item xs={12} sm={6}>
            <Typography fontWeight={600} mb={1}>
              Select Class
            </Typography>
            <Select
              fullWidth
              value={selectedClass}
              sx={{
                borderRadius: "999px",
              }}
              disabled={role === "staff"} // 🔒 Disable for both if needed
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setErrors((prev) => ({
                  ...prev,
                  selectedClass: "",
                }));
              }}
            >
              <MenuItem value="">Select Class</MenuItem>
              {classList.map((c) => (
                <MenuItem key={c.guid} value={c.guid}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            {errors.selectedClass && (
              <FormHelperText error>{errors.selectedClass}</FormHelperText>
            )}
          </Grid>

          {/* DATE */}
          <Grid item xs={12} sm={6}>
            <Typography fontWeight={600} mb={1}>
              Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={date}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "999px",
                },
              }}
              onChange={(e) => setDate(e.target.value)}
              inputProps={{
                max: new Date().toISOString().split("T")[0],
              }}
            />
          </Grid>
        </Grid>
      </Card>

      {/* STUDENTS */}
      <Card sx={{ p: 3, borderRadius: "16px" }}>
        {students.length === 0 ? (
          <Typography>No students found</Typography>
        ) : (
          students.map((stu) => {
            const att = attendance[stu.guid] || { status: "present", check_in: "", check_out: "" };
            const isPresent = att.status === "present";

            // Calculate total hours dynamically
            let displayHours = "";
            if (isPresent && att.check_in && att.check_out) {
              const [h1, m1] = att.check_in.split(":").map(Number);
              const [h2, m2] = att.check_out.split(":").map(Number);
              const diffMinutes = (h2 * 60 + m2) - (h1 * 60 + m1);
              if (diffMinutes > 0) {
                displayHours = (diffMinutes / 60).toFixed(2) + " hrs";
              } else {
                displayHours = "0 hrs";
              }
            }

            return (
              <Box
                key={stu.guid}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", md: "center" },
                  p: 2,
                  mb: 2,
                  borderRadius: 3,
                  backgroundColor: isPresent ? "rgba(76, 175, 80, 0.04)" : "rgba(244, 67, 54, 0.04)",
                  border: `1px solid ${isPresent ? "rgba(76, 175, 80, 0.12)" : "rgba(244, 67, 54, 0.12)"}`
                }}
              >
                <Typography fontWeight={600} sx={{ minWidth: 200, mb: { xs: 1, md: 0 } }}>
                  {stu.name}
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                  {!isPresent && (
                    <Chip
                      label="Absent"
                      color="error"
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2 }}
                    />
                  )}
                  {isPresent && (
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {!att.check_in && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2 }}
                          onClick={() => {
                            const newTime = getCurrentTime();
                            const newAtt = { ...att, check_in: newTime };
                            setAttendance((prev) => ({
                              ...prev,
                              [stu.guid]: newAtt
                            }));
                            saveSingleStudentAttendance(stu.guid, newAtt);
                          }}
                        >
                          Check-In
                        </Button>
                      )}

                      {att.check_in && !att.check_out && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip label={`In: ${att.check_in}`} variant="outlined" size="small" />
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            sx={{ borderRadius: 2 }}
                            onClick={() => {
                              const newTime = getCurrentTime();
                              const newAtt = { ...att, check_out: newTime };
                              setAttendance((prev) => ({
                                ...prev,
                                [stu.guid]: newAtt
                              }));
                              saveSingleStudentAttendance(stu.guid, newAtt);
                            }}
                          >
                            Check-Out
                          </Button>
                        </Box>
                      )}

                      {att.check_in && att.check_out && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip label={`In: ${att.check_in}`} variant="outlined" size="small" />
                          <Chip label={`Out: ${att.check_out}`} variant="outlined" size="small" />
                          {displayHours && (
                            <Chip
                              label={displayHours}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>
                      )}

                      {/* {(att.check_in || att.check_out) && (
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => {
                            const newAtt = { ...att, check_in: "", check_out: "" };
                            setAttendance((prev) => ({
                              ...prev,
                              [stu.guid]: newAtt
                            }));
                            saveSingleStudentAttendance(stu.guid, newAtt);
                          }}
                          sx={{ minWidth: "auto", p: 0.5 }}
                        >
                          Reset
                        </Button>
                      )} */}
                    </Box>
                  )}

                  {!isPresent && att.reason && (
                    <Typography variant="body2" color="error" sx={{ fontStyle: "italic" }}>
                      Reason: {att.reason}
                    </Typography>
                  )}

                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      const curAtt = attendance[stu.guid] || { status: "present", check_in: "", check_out: "", reason: "" };
                      setTempAtt({
                        status: curAtt.status || "present",
                        check_in: curAtt.check_in || "",
                        check_out: curAtt.check_out || "",
                        reason: curAtt.reason || "",
                      });
                      setEditStudent(stu);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 3, borderRadius: "999px" }}
          onClick={submitAttendance}
        >
          Save Attendance
        </Button>
      </Card>

      {/* ABSENT REASON */}
      <Dialog
        open={reasonDialog.open}
        onClose={() => setReasonDialog({ open: false })}
      >
        <DialogTitle>Reason for Absence</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={reasonDialog.reason}
            onChange={(e) =>
              setReasonDialog((prev) => ({
                ...prev,
                reason: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialog({ open: false })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setAttendance((prev) => ({
                ...prev,
                [reasonDialog.studentId]: {
                  ...prev[reasonDialog.studentId],
                  status: "absent",
                  reason: reasonDialog.reason,
                  check_in: "",
                  check_out: "",
                },
              }));
              setReasonDialog({
                open: false,
                studentId: null,
                reason: "",
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* ADVANCED EDIT DIALOG */}
      <Dialog
        open={Boolean(editStudent)}
        onClose={() => setEditStudent(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Attendance - {editStudent?.name}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          <Box display="flex" gap={2}>
            <Button
              variant={tempAtt.status === "present" ? "contained" : "outlined"}
              color="success"
              fullWidth
              onClick={() =>
                setTempAtt((prev) => ({
                  ...prev,
                  status: "present",
                  reason: ""
                }))
              }
            >
              Present
            </Button>
            <Button
              variant={tempAtt.status === "absent" ? "contained" : "outlined"}
              color="error"
              fullWidth
              onClick={() =>
                setTempAtt((prev) => ({
                  ...prev,
                  status: "absent",
                  check_in: "",
                  check_out: ""
                }))
              }
            >
              Absent
            </Button>
          </Box>

          {tempAtt.status === "present" && (
            <Box display="flex" gap={2}>
              <TextField
                type="time"
                label="Check In"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={tempAtt.check_in || ""}
                onChange={(e) =>
                  setTempAtt((prev) => ({
                    ...prev,
                    check_in: e.target.value
                  }))
                }
              />
              <TextField
                type="time"
                label="Check Out"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={tempAtt.check_out || ""}
                onChange={(e) =>
                  setTempAtt((prev) => ({
                    ...prev,
                    check_out: e.target.value
                  }))
                }
              />
            </Box>
          )}

          {tempAtt.status === "absent" && (
            <TextField
              label="Reason for Absence"
              multiline
              rows={3}
              fullWidth
              value={tempAtt.reason || ""}
              onChange={(e) =>
                setTempAtt((prev) => ({
                  ...prev,
                  reason: e.target.value
                }))
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (window.confirm(`Are you sure you want to save the attendance status for ${editStudent?.name}?`)) {
                setAttendance((prev) => ({
                  ...prev,
                  [editStudent.guid]: tempAtt
                }));
                saveSingleStudentAttendance(editStudent.guid, tempAtt);
                setEditStudent(null);
              }
            }}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
